import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Wand2,
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  Copy,
  Download,
  RefreshCw,
  Sparkles,
  Bot,
  FileCode,
  FileEdit,
  Save,
  Search,
  Zap,
  Headphones,
  PenTool,
  Code,
  Network,
  Lightbulb,
  ChevronRight,
} from 'lucide-react';
import api from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import type {
  TemplateInfo,
  TemplateCategory,
  FollowUpQuestion,
  Suggestion,
  ClassifyResponse,
  AvailableResources,
} from '@/services/agentWizardApi';
import { AgentConfigForm } from '@/components/wizard/AgentConfigForm';
import { GraphBuilder, graphConfigToReactFlow, reactFlowToGraphConfig } from '@/components/wizard/GraphBuilder';
import type { GraphConfig, GraphNodeConfig, GraphEdgeConfig } from '@/components/wizard/GraphBuilder';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ParsedConfig {
  name?: string;
  model?: string;
  type?: string;
  tools?: string[];
  description?: string;
}

type WizardStep = 0 | 1 | 2;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STEP_LABELS = ['Choose Method', 'Configure', 'Review & Save'] as const;

const STARTER_TEMPLATE = `---
name: my-agent
model: openrouter:google/gemini-3-flash-preview
description: ""
tools: []
---

You are a helpful assistant.`;

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  research: <Search className="h-4 w-4" />,
  automation: <Zap className="h-4 w-4" />,
  support: <Headphones className="h-4 w-4" />,
  content: <PenTool className="h-4 w-4" />,
  coding: <Code className="h-4 w-4" />,
  'multi-agent': <Network className="h-4 w-4" />,
  'your-agents': <Bot className="h-4 w-4" />,
};

const QUICKSTART_PLACEHOLDERS = [
  'Research agent for competitive analysis...',
  'Support bot that handles customer questions...',
  'Code reviewer for Python pull requests...',
  'Blog writer for technical content...',
  'Data pipeline that processes CSV files...',
];

function getErrorMessage(err: any): string {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message ?? 'Unknown error';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d: any) => d.msg ?? JSON.stringify(d)).join('; ');
  return JSON.stringify(detail);
}

function parseConfigFields(config: string): ParsedConfig {
  if (!config) return {};
  const get = (key: string): string | undefined => {
    const match = config.match(new RegExp(`^${key}:\\s*(.+)$`, 'm'));
    return match?.[1]?.trim().replace(/^["']|["']$/g, '');
  };

  const toolsMatch = config.match(/^tools:\s*\n((?:\s+-\s+.+\n?)*)/m);
  const tools = toolsMatch
    ? toolsMatch[1]
        .split('\n')
        .map((l) => l.replace(/^\s*-\s*/, '').trim())
        .filter(Boolean)
    : undefined;

  return {
    name: get('name'),
    model: get('model'),
    type: get('type'),
    tools: tools && tools.length > 0 ? tools : undefined,
    description: get('description'),
  };
}

// ---------------------------------------------------------------------------
// Graph config <-> YAML extraction
// ---------------------------------------------------------------------------

const EMPTY_GRAPH_CONFIG: GraphConfig = {
  nodes: [],
  edges: [{ from: 'START', to: 'END' }],
};

/** Crude extraction of graph: section from a YAML frontmatter config string. */
function extractGraphConfig(yamlConfig: string): GraphConfig {
  // Match the graph: block in YAML frontmatter
  const graphMatch = yamlConfig.match(/^graph:\s*\n([\s\S]*?)(?=^[a-z_]+:|\n---)/m);
  if (!graphMatch) return EMPTY_GRAPH_CONFIG;

  const block = graphMatch[1];
  const nodes: GraphNodeConfig[] = [];
  const edges: GraphEdgeConfig[] = [];

  // Parse nodes section
  const nodesMatch = block.match(/nodes:\s*\n([\s\S]*?)(?=\s+edges:|$)/);
  if (nodesMatch) {
    const nodeBlocks = nodesMatch[1].split(/\n\s+-\s+name:/);
    nodeBlocks.forEach((nb, idx) => {
      const text = idx === 0 ? nb : `name:${nb}`;
      const getName = (k: string) => {
        const m = text.match(new RegExp(`${k}:\\s*(.+)`));
        return m?.[1]?.trim().replace(/^["']|["']$/g, '');
      };
      const name = getName('name');
      const type = getName('type');
      if (name && type) {
        // Extract list fields
        const getList = (k: string): string[] | undefined => {
          const lm = text.match(new RegExp(`${k}:\\s*\\n((?:\\s+-\\s+.+\\n?)*)`, 'm'));
          if (!lm) return undefined;
          return lm[1].split('\n').map(l => l.replace(/^\s*-\s*/, '').trim()).filter(Boolean);
        };
        nodes.push({
          name,
          type,
          model: getName('model'),
          prompt: getName('prompt'),
          tools: getList('tools'),
          mcp_servers: getList('mcp_servers'),
          function: getName('function'),
          router: getName('router'),
          hitl_before: getName('hitl_before') === 'true',
          hitl_after: getName('hitl_after') === 'true',
        });
      }
    });
  }

  // Parse edges section
  const edgesMatch = block.match(/edges:\s*\n([\s\S]*?)$/);
  if (edgesMatch) {
    const edgeLines = edgesMatch[1].split(/\n\s+-\s+from:/);
    edgeLines.forEach((el, idx) => {
      const text = idx === 0 ? el : `from:${el}`;
      const getVal = (k: string) => {
        const m = text.match(new RegExp(`${k}:\\s*(.+)`));
        return m?.[1]?.trim().replace(/^["']|["']$/g, '');
      };
      const from = getVal('from');
      const to = getVal('to');
      if (from && to) {
        edges.push({ from, to, condition: getVal('condition') });
      }
    });
  }

  return { nodes, edges: edges.length > 0 ? edges : [{ from: 'START', to: 'END' }] };
}

/** Serializes a GraphConfig back into a YAML graph: block string. */
function graphConfigToYaml(gc: GraphConfig): string {
  let yaml = 'graph:\n  nodes:\n';
  gc.nodes.forEach((n) => {
    yaml += `    - name: ${n.name}\n`;
    yaml += `      type: ${n.type}\n`;
    if (n.model) yaml += `      model: ${n.model}\n`;
    if (n.prompt) yaml += `      prompt: "${n.prompt.replace(/"/g, '\\"')}"\n`;
    if (n.function) yaml += `      function: ${n.function}\n`;
    if (n.router) yaml += `      router: ${n.router}\n`;
    if (n.tools && n.tools.length > 0) {
      yaml += '      tools:\n';
      n.tools.forEach((t) => { yaml += `        - ${t}\n`; });
    }
    if (n.mcp_servers && n.mcp_servers.length > 0) {
      yaml += '      mcp_servers:\n';
      n.mcp_servers.forEach((s) => { yaml += `        - ${s}\n`; });
    }
    if (n.workers && n.workers.length > 0) {
      yaml += '      workers:\n';
      n.workers.forEach((w) => { yaml += `        - name: ${w.name}\n`; });
    }
    if (n.hitl_before) yaml += '      hitl_before: true\n';
    if (n.hitl_after) yaml += '      hitl_after: true\n';
  });
  yaml += '  edges:\n';
  gc.edges.forEach((e) => {
    yaml += `    - from: ${e.from}\n`;
    yaml += `      to: ${e.to}\n`;
    if (e.condition) yaml += `      condition: ${e.condition}\n`;
  });
  return yaml;
}

/** Replaces the graph: section in a full YAML config, or appends it. */
function updateConfigWithGraph(fullConfig: string, gc: GraphConfig): string {
  const graphYaml = graphConfigToYaml(gc);
  // Try to replace existing graph: block
  const graphRegex = /^graph:\s*\n[\s\S]*?(?=^[a-z_]+:(?!\s)|\n---)/m;
  if (graphRegex.test(fullConfig)) {
    return fullConfig.replace(graphRegex, graphYaml);
  }
  // Append before closing ---
  const closingIdx = fullConfig.lastIndexOf('\n---');
  if (closingIdx > 0) {
    return fullConfig.slice(0, closingIdx) + '\n' + graphYaml + fullConfig.slice(closingIdx);
  }
  return fullConfig + '\n' + graphYaml;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StepIndicator({ current }: { current: WizardStep }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {STEP_LABELS.map((label, idx) => {
        const isCompleted = idx < current;
        const isCurrent = idx === current;
        return (
          <React.Fragment key={label}>
            {idx > 0 && (
              <div
                className={`h-px w-10 ${
                  isCompleted ? 'bg-primary' : 'bg-muted-foreground/30'
                }`}
              />
            )}
            <div className="flex items-center gap-2">
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors ${
                  isCompleted
                    ? 'bg-primary text-primary-foreground'
                    : isCurrent
                      ? 'border-2 border-primary text-primary'
                      : 'border border-muted-foreground/30 text-muted-foreground'
                }`}
              >
                {isCompleted ? <Check className="h-4 w-4" /> : idx + 1}
              </div>
              <span
                className={`text-sm hidden sm:inline ${
                  isCurrent ? 'font-semibold text-foreground' : 'text-muted-foreground'
                }`}
              >
                {label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function ConfigPreview({ config }: { config: string }) {
  return (
    <pre className="rounded-md border bg-muted/50 p-4 text-sm font-mono whitespace-pre-wrap overflow-auto max-h-[28rem]">
      {config}
    </pre>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function AgentWizardPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  // Wizard state
  const [step, setStep] = useState<WizardStep>(0);

  // Step 0 - method choice
  const [method, setMethod] = useState<'describe' | 'template' | 'manual' | null>(null);

  // Quick-start bar
  const [quickInput, setQuickInput] = useState('');
  const [placeholderIdx, setPlaceholderIdx] = useState(0);

  // Step 0 - describe path
  const [description, setDescription] = useState('');
  const [agentName, setAgentName] = useState('');
  const [selectedModel, setSelectedModel] = useState('');

  // Classify results (shown after describe)
  const [classifyResult, setClassifyResult] = useState<ClassifyResponse | null>(null);
  const [classifying, setClassifying] = useState(false);
  const [paramValues, setParamValues] = useState<Record<string, string>>({});
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  // Step 0 - template path
  const [templates, setTemplates] = useState<TemplateInfo[]>([]);
  const [categories, setCategories] = useState<TemplateCategory[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateInfo | null>(null);
  const [templateParamValues, setTemplateParamValues] = useState<Record<string, string>>({});

  // Step 0 - manual path
  const [manualConfig, setManualConfig] = useState('');

  // Step 1 - config
  const [config, setConfig] = useState('');
  const [generating, setGenerating] = useState(false);
  const [refineFeedback, setRefineFeedback] = useState('');
  const [refining, setRefining] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [resources, setResources] = useState<AvailableResources | null>(null);

  // Graph builder state
  const [graphEditorMode, setGraphEditorMode] = useState<'visual' | 'yaml'>('visual');

  // Rotate quickstart placeholder
  useEffect(() => {
    const timer = setInterval(() => {
      setPlaceholderIdx((i) => (i + 1) % QUICKSTART_PLACEHOLDERS.length);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  // Fetch resources once when entering step 1
  useEffect(() => {
    if (step !== 1 || resources) return;
    api.get('/agent-wizard/resources').then((res) => {
      setResources(res.data);
    }).catch(() => {});
  }, [step, resources]);

  // Fetch suggestions when config changes in step 1
  useEffect(() => {
    if (step !== 1 || !config || config.length < 20) return;
    const timer = setTimeout(async () => {
      try {
        const res = await api.post('/agent-wizard/suggest', { agent_config: config });
        setSuggestions(res.data.suggestions ?? []);
      } catch {
        // Non-critical
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [config, step]);

  // ---------------------------------------------------------------------------
  // API helpers
  // ---------------------------------------------------------------------------

  const fetchTemplates = useCallback(async () => {
    if (templates.length > 0) return;
    setTemplatesLoading(true);
    try {
      const res = await api.get('/agent-wizard/templates', { params: { source: 'all' } });
      setTemplates(res.data.templates ?? []);
      setCategories(res.data.categories ?? []);
    } catch (err: any) {
      toast({
        title: 'Failed to load templates',
        description: getErrorMessage(err),
        variant: 'destructive',
      });
    } finally {
      setTemplatesLoading(false);
    }
  }, [templates.length, toast]);

  const handleQuickStart = useCallback(async () => {
    const text = quickInput.trim();
    if (!text) return;
    setDescription(text);
    setMethod('describe');
    setClassifying(true);
    try {
      const res = await api.post('/agent-wizard/classify', { description: text });
      setClassifyResult(res.data);
      // Pre-fill inferred parameters
      setParamValues(res.data.inferred_parameters ?? {});
      if (res.data.suggested_template_ids?.length > 0) {
        setSelectedTemplateId(res.data.suggested_template_ids[0]);
      }
    } catch (err: any) {
      toast({
        title: 'Classification failed',
        description: getErrorMessage(err) + ' — you can still describe your agent manually.',
        variant: 'destructive',
      });
      setClassifyResult(null);
    } finally {
      setClassifying(false);
    }
  }, [quickInput, toast]);

  const classifyDescription = useCallback(async () => {
    if (!description.trim()) return;
    setClassifying(true);
    try {
      const res = await api.post('/agent-wizard/classify', { description: description.trim() });
      setClassifyResult(res.data);
      setParamValues(res.data.inferred_parameters ?? {});
      if (res.data.suggested_template_ids?.length > 0) {
        setSelectedTemplateId(res.data.suggested_template_ids[0]);
      }
    } catch (err: any) {
      toast({
        title: 'Classification failed — falling back to direct generation',
        description: getErrorMessage(err),
        variant: 'destructive',
      });
      setClassifyResult(null);
    } finally {
      setClassifying(false);
    }
  }, [description, toast]);

  const instantiateAndProceed = useCallback(async () => {
    if (!selectedTemplateId) return;
    setGenerating(true);
    try {
      const res = await api.post('/agent-wizard/instantiate', {
        template_id: selectedTemplateId,
        parameters: paramValues,
        custom_prompt_instructions: description.trim().length > 50 ? description.trim() : undefined,
      });
      let resultConfig = res.data.agent_config ?? STARTER_TEMPLATE;
      // Apply model preference if user selected one
      if (selectedModel && resultConfig.includes('model:')) {
        resultConfig = resultConfig.replace(
          /^model:\s*.+$/m,
          `model: ${selectedModel}`
        );
      }
      setConfig(resultConfig);
      setStep(1);
    } catch (err: any) {
      toast({
        title: 'Generation failed',
        description: getErrorMessage(err) + ' — falling back to direct generation.',
        variant: 'destructive',
      });
      // Fallback to full generate
      await generateConfigDirect();
    } finally {
      setGenerating(false);
    }
  }, [selectedTemplateId, paramValues, description, selectedModel, toast]);

  const generateConfigDirect = useCallback(async () => {
    if (!description.trim()) {
      toast({ title: 'Please describe your agent first', variant: 'destructive' });
      return;
    }
    setGenerating(true);
    try {
      const payload: Record<string, string> = { description: description.trim() };
      if (selectedModel) payload.model_preference = selectedModel;

      const res = await api.post('/agent-wizard/generate', payload);
      setConfig(res.data.agent_config ?? STARTER_TEMPLATE);
      setStep(1);
    } catch (err: any) {
      toast({
        title: 'Generation failed',
        description: getErrorMessage(err) + ' — falling back to starter template.',
        variant: 'destructive',
      });
      setConfig(STARTER_TEMPLATE);
      setStep(1);
    } finally {
      setGenerating(false);
    }
  }, [description, selectedModel, toast]);

  const instantiateTemplate = useCallback(async () => {
    if (!selectedTemplate) return;
    setGenerating(true);
    try {
      const res = await api.post('/agent-wizard/instantiate', {
        template_id: selectedTemplate.id,
        parameters: templateParamValues,
      });
      setConfig(res.data.agent_config ?? selectedTemplate.agent_config);
      setStep(1);
    } catch (err: any) {
      // Fallback: use raw template config
      setConfig(selectedTemplate.agent_config);
      setStep(1);
    } finally {
      setGenerating(false);
    }
  }, [selectedTemplate, templateParamValues]);

  const refineConfig = useCallback(async () => {
    if (!refineFeedback.trim()) return;
    setRefining(true);
    try {
      const res = await api.post('/agent-wizard/refine', {
        agent_config: config,
        instructions: refineFeedback.trim(),
      });
      setConfig(res.data.agent_config ?? config);
      setRefineFeedback('');
      toast({ title: 'Config updated' });
    } catch (err: any) {
      toast({
        title: 'Refinement failed',
        description: getErrorMessage(err),
        variant: 'destructive',
      });
    } finally {
      setRefining(false);
    }
  }, [config, refineFeedback, toast]);

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------

  const canGoNext = (): boolean => {
    if (step === 0) {
      if (method === 'describe') {
        if (classifyResult && selectedTemplateId) return true;
        return description.trim().length > 0;
      }
      if (method === 'template') return selectedTemplate !== null;
      if (method === 'manual') return manualConfig.trim().length > 0;
      return false;
    }
    if (step === 1) return (config ?? '').length > 0;
    return false;
  };

  const handleNext = async () => {
    if (step === 0) {
      if (method === 'describe') {
        if (classifyResult && selectedTemplateId) {
          await instantiateAndProceed();
        } else {
          await generateConfigDirect();
        }
        return;
      }
      if (method === 'template' && selectedTemplate) {
        if (selectedTemplate.parameters?.length > 0) {
          await instantiateTemplate();
        } else {
          setConfig(selectedTemplate.agent_config);
          setStep(1);
        }
        return;
      }
      if (method === 'manual') {
        setConfig(manualConfig);
        setStep(1);
        return;
      }
    }
    if (step === 1) {
      setStep(2);
    }
  };

  const handleBack = () => {
    if (step === 1) {
      setStep(0);
    } else if (step === 2) {
      setStep(1);
    }
  };

  // ---------------------------------------------------------------------------
  // Final actions
  // ---------------------------------------------------------------------------

  const copyToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(config);
      toast({ title: 'Copied to clipboard' });
    } catch {
      toast({ title: 'Failed to copy', variant: 'destructive' });
    }
  }, [config, toast]);

  const downloadConfig = useCallback(() => {
    const parsed = parseConfigFields(config);
    const filename = parsed.name
      ? `${parsed.name.toLowerCase().replace(/\s+/g, '-')}.md`
      : 'agent-config.md';
    const blob = new Blob([config], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }, [config]);

  const openInIDE = useCallback(() => {
    navigate('/developer/ide');
  }, [navigate]);

  const saveToContentStore = async () => {
    const parsed = parseConfigFields(config);
    const name = parsed.name || agentName || 'new-agent';
    try {
      await api.post('/content/items', {
        name,
        content_type: 'agent',
        folder: '/agents',
        content: btoa(unescape(encodeURIComponent(config))),  // UTF-8 safe base64
        publish: true,
      });
      toast({ title: 'Agent saved!', description: `Saved as ${name} in /agents` });
      navigate('/agents');
    } catch (err: any) {
      toast({ title: 'Failed to save', description: err?.response?.data?.detail ?? err.message, variant: 'destructive' });
    }
  };

  // ---------------------------------------------------------------------------
  // Helper: find template by ID from loaded templates
  // ---------------------------------------------------------------------------

  const getTemplateById = (id: string): TemplateInfo | undefined =>
    templates.find((t) => t.id === id);

  // ---------------------------------------------------------------------------
  // Render: Step 0
  // ---------------------------------------------------------------------------

  const renderStep0 = () => {
    const filteredTemplates = selectedCategory === 'all'
      ? templates
      : templates.filter((t) => t.category === selectedCategory);

    const matchedTemplate = selectedTemplateId ? getTemplateById(selectedTemplateId) : null;

    return (
      <div className="space-y-6">
        {/* Quick-start bar */}
        <div className="max-w-2xl mx-auto">
          <div className="relative">
            <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              className="pl-10 pr-24 h-12 text-base"
              placeholder={QUICKSTART_PLACEHOLDERS[placeholderIdx]}
              value={quickInput}
              onChange={(e) => setQuickInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && quickInput.trim()) {
                  handleQuickStart();
                }
              }}
            />
            <Button
              size="sm"
              className="absolute right-1.5 top-1/2 -translate-y-1/2"
              disabled={!quickInput.trim() || classifying}
              onClick={handleQuickStart}
            >
              {classifying ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Create
                  <ChevronRight className="h-4 w-4 ml-1" />
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground text-center mt-2">
            Describe what your agent should do and we'll match it to the best template
          </p>
        </div>

        {/* Classify result (shown when quick-start or describe classification completes) */}
        {classifyResult && method === 'describe' && !classifying && (
          <Card className="max-w-3xl mx-auto border-primary/30">
            <CardContent className="pt-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                  <Sparkles className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">
                    We matched your intent to{' '}
                    <span className="text-primary">{matchedTemplate?.name ?? classifyResult.category}</span>
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Confidence: {Math.round(classifyResult.confidence * 100)}%
                    {classifyResult.suggested_template_ids.length > 1 && (
                      <> — {classifyResult.suggested_template_ids.length} templates match</>
                    )}
                  </p>
                </div>
              </div>

              {/* Template selector if multiple matches */}
              {classifyResult.suggested_template_ids.length > 1 && (
                <div className="space-y-2">
                  <Label>Choose template</Label>
                  <Select value={selectedTemplateId ?? ''} onValueChange={setSelectedTemplateId}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {classifyResult.suggested_template_ids.map((tid) => {
                        const t = getTemplateById(tid);
                        return (
                          <SelectItem key={tid} value={tid}>
                            {t?.name ?? tid}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Parameter form */}
              {matchedTemplate?.parameters && matchedTemplate.parameters.length > 0 && (
                <div className="space-y-3">
                  <Separator />
                  <Label className="text-sm font-medium">Customize</Label>
                  {matchedTemplate.parameters.map((param) => (
                    <div key={param.name} className="space-y-1">
                      <Label htmlFor={`param-${param.name}`} className="text-sm">
                        {param.label}
                        {param.required && <span className="text-destructive ml-1">*</span>}
                      </Label>
                      {param.type === 'select' && param.options ? (
                        <Select
                          value={paramValues[param.name] ?? param.default ?? ''}
                          onValueChange={(v) => setParamValues((prev) => ({ ...prev, [param.name]: v }))}
                        >
                          <SelectTrigger id={`param-${param.name}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {param.options.map((opt) => (
                              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          id={`param-${param.name}`}
                          placeholder={param.description ?? ''}
                          value={paramValues[param.name] ?? param.default ?? ''}
                          onChange={(e) => setParamValues((prev) => ({ ...prev, [param.name]: e.target.value }))}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Follow-up questions */}
              {classifyResult.follow_up_questions.length > 0 && (
                <div className="space-y-3">
                  <Separator />
                  <Label className="text-sm font-medium">A few more details</Label>
                  {classifyResult.follow_up_questions.map((fq) => (
                    <div key={fq.parameter_name} className="space-y-1">
                      <Label htmlFor={`fq-${fq.parameter_name}`} className="text-sm">
                        {fq.question}
                      </Label>
                      {fq.options ? (
                        <Select
                          value={paramValues[fq.parameter_name] ?? ''}
                          onValueChange={(v) => setParamValues((prev) => ({ ...prev, [fq.parameter_name]: v }))}
                        >
                          <SelectTrigger id={`fq-${fq.parameter_name}`}>
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            {fq.options.map((opt) => (
                              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          id={`fq-${fq.parameter_name}`}
                          value={paramValues[fq.parameter_name] ?? ''}
                          onChange={(e) => setParamValues((prev) => ({ ...prev, [fq.parameter_name]: e.target.value }))}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-2 pt-2">
                <Label htmlFor="classify-model" className="text-sm shrink-0">Model</Label>
                <Select value={selectedModel} onValueChange={setSelectedModel}>
                  <SelectTrigger id="classify-model" className="w-[200px]">
                    <SelectValue placeholder="Auto-select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="openrouter:google/gemini-3-flash-preview">Gemini 3 Flash</SelectItem>
                    <SelectItem value="openrouter:google/gemini-3-pro-preview">Gemini 3 Pro</SelectItem>
                    <SelectItem value="openrouter:anthropic/claude-sonnet-4.5">Claude Sonnet 4.5</SelectItem>
                    <SelectItem value="openrouter:anthropic/claude-opus-4.5">Claude Opus 4.5</SelectItem>
                    <SelectItem value="openrouter:openai/gpt-5.2">GPT 5.2</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Method cards (shown when no quick-start result) */}
        {!classifyResult && (
          <>
            <div className="relative">
              <Separator className="my-4" />
              <span className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 bg-background px-3 text-xs text-muted-foreground uppercase">
                or choose a method
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <Card
                className={`cursor-pointer transition-all hover:shadow-md hover:border-primary/50 ${
                  method === 'describe' ? 'border-primary ring-2 ring-primary/20' : ''
                }`}
                onClick={() => {
                  setMethod('describe');
                  setClassifyResult(null);
                  setSelectedTemplate(null);
                }}
              >
                <CardHeader className="text-center pb-3">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                    <Sparkles className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-lg">Describe Your Agent</CardTitle>
                  <CardDescription>
                    Tell us what your agent should do and AI will classify your intent and generate the config.
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card
                className={`cursor-pointer transition-all hover:shadow-md hover:border-primary/50 ${
                  method === 'template' ? 'border-primary ring-2 ring-primary/20' : ''
                }`}
                onClick={() => {
                  setMethod('template');
                  setClassifyResult(null);
                  fetchTemplates();
                }}
              >
                <CardHeader className="text-center pb-3">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                    <FileCode className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-lg">Start From Template</CardTitle>
                  <CardDescription>
                    Pick from {templates.length || '15+'} templates across research, coding, support, and more.
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card
                className={`cursor-pointer transition-all hover:shadow-md hover:border-primary/50 ${
                  method === 'manual' ? 'border-primary ring-2 ring-primary/20' : ''
                }`}
                onClick={() => {
                  setMethod('manual');
                  setClassifyResult(null);
                  setSelectedTemplate(null);
                }}
              >
                <CardHeader className="text-center pb-3">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                    <FileEdit className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-lg">Write Manually</CardTitle>
                  <CardDescription>
                    Paste or write your agent markdown configuration directly.
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>
          </>
        )}

        {/* Describe form */}
        {method === 'describe' && !classifyResult && (
          <Card className="max-w-3xl mx-auto mt-6">
            <CardContent className="pt-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="agent-description">
                  Describe what your agent should do <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  id="agent-description"
                  placeholder="e.g. A customer support agent that can look up orders, answer product questions, and escalate issues to a human when needed..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  className="resize-none"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="agent-name">Agent Name (optional)</Label>
                  <Input
                    id="agent-name"
                    placeholder="My Agent"
                    value={agentName}
                    onChange={(e) => setAgentName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="agent-model">Model (optional)</Label>
                  <Select value={selectedModel} onValueChange={setSelectedModel}>
                    <SelectTrigger id="agent-model">
                      <SelectValue placeholder="Auto-select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="openrouter:google/gemini-3-flash-preview">Gemini 3 Flash (Recommended)</SelectItem>
                      <SelectItem value="openrouter:google/gemini-3-pro-preview">Gemini 3 Pro</SelectItem>
                      <SelectItem value="openrouter:anthropic/claude-sonnet-4.5">Claude Sonnet 4.5</SelectItem>
                      <SelectItem value="openrouter:anthropic/claude-opus-4.5">Claude Opus 4.5</SelectItem>
                      <SelectItem value="openrouter:openai/gpt-5.2">GPT 5.2</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button
                onClick={classifyDescription}
                disabled={classifying || !description.trim()}
                className="w-full"
              >
                {classifying ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Find Best Template
                  </>
                )}
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                Or click Next to generate directly with AI
              </p>
            </CardContent>
          </Card>
        )}

        {/* Template picker with categories */}
        {method === 'template' && (
          <div className="max-w-4xl mx-auto mt-6 space-y-4">
            {templatesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">Loading templates...</span>
              </div>
            ) : templates.length === 0 ? (
              <Alert>
                <AlertDescription>
                  No templates available. Try describing your agent instead.
                </AlertDescription>
              </Alert>
            ) : (
              <>
                {/* Category tabs */}
                <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
                  <TabsList className="flex flex-wrap h-auto gap-1">
                    <TabsTrigger value="all" className="text-xs">All</TabsTrigger>
                    {categories.map((cat) => (
                      <TabsTrigger key={cat.id} value={cat.id} className="text-xs gap-1">
                        {CATEGORY_ICONS[cat.id]}
                        {cat.name}
                      </TabsTrigger>
                    ))}
                    {templates.some((t) => t.category === 'your-agents') && (
                      <TabsTrigger value="your-agents" className="text-xs gap-1">
                        {CATEGORY_ICONS['your-agents']}
                        Your Agents
                      </TabsTrigger>
                    )}
                  </TabsList>
                </Tabs>

                {/* Template cards */}
                <div className="grid grid-cols-1 gap-3">
                  {filteredTemplates.map((tpl) => (
                    <Card
                      key={tpl.id}
                      className={`cursor-pointer transition-all hover:shadow-sm hover:border-primary/50 ${
                        selectedTemplate?.id === tpl.id
                          ? 'border-primary ring-2 ring-primary/20'
                          : ''
                      }`}
                      onClick={() => {
                        setSelectedTemplate(tpl);
                        // Pre-fill defaults
                        const defaults: Record<string, string> = {};
                        tpl.parameters?.forEach((p) => {
                          if (p.default) defaults[p.name] = p.default;
                        });
                        setTemplateParamValues(defaults);
                      }}
                    >
                      <CardContent className="flex items-center gap-4 py-4">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted">
                          {CATEGORY_ICONS[tpl.category] ?? <Bot className="h-5 w-5 text-muted-foreground" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{tpl.name}</span>
                            <Badge variant="secondary" className="text-xs">
                              {tpl.complexity === 'graph' ? 'Multi-Agent' : 'Simple'}
                            </Badge>
                            {tpl.tags?.slice(0, 2).map((tag) => (
                              <Badge key={tag} variant="outline" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                          <p className="text-sm text-muted-foreground truncate">
                            {tpl.description}
                          </p>
                        </div>
                        {selectedTemplate?.id === tpl.id && (
                          <Check className="h-5 w-5 text-primary shrink-0" />
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Parameter form for selected template */}
                {selectedTemplate && selectedTemplate.parameters?.length > 0 && (
                  <Card className="border-primary/30">
                    <CardContent className="pt-6 space-y-3">
                      <Label className="text-sm font-medium">
                        Customize {selectedTemplate.name}
                      </Label>
                      {selectedTemplate.parameters.map((param) => (
                        <div key={param.name} className="space-y-1">
                          <Label htmlFor={`tpl-param-${param.name}`} className="text-sm">
                            {param.label}
                            {param.required && <span className="text-destructive ml-1">*</span>}
                          </Label>
                          {param.type === 'select' && param.options ? (
                            <Select
                              value={templateParamValues[param.name] ?? param.default ?? ''}
                              onValueChange={(v) =>
                                setTemplateParamValues((prev) => ({ ...prev, [param.name]: v }))
                              }
                            >
                              <SelectTrigger id={`tpl-param-${param.name}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {param.options.map((opt) => (
                                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              id={`tpl-param-${param.name}`}
                              placeholder={param.description ?? ''}
                              value={templateParamValues[param.name] ?? param.default ?? ''}
                              onChange={(e) =>
                                setTemplateParamValues((prev) => ({ ...prev, [param.name]: e.target.value }))
                              }
                            />
                          )}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>
        )}

        {/* Manual config editor */}
        {method === 'manual' && (
          <Card className="max-w-3xl mx-auto mt-6">
            <CardContent className="pt-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="manual-config">
                  Agent Configuration (Markdown) <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  id="manual-config"
                  placeholder={STARTER_TEMPLATE}
                  value={manualConfig}
                  onChange={(e) => setManualConfig(e.target.value)}
                  rows={12}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Paste or write your agent markdown config. Use YAML frontmatter for settings and the body for the system prompt.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Render: Step 1
  // ---------------------------------------------------------------------------

  const renderStep1 = () => {
    const parsed = parseConfigFields(config);

    return (
      <div className="space-y-6 max-w-4xl mx-auto">
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold tracking-tight">Configure Your Agent</h2>
          <p className="text-muted-foreground">
            Review the generated configuration and refine it as needed.
          </p>
        </div>

        {/* Key fields summary */}
        <div className="flex flex-wrap gap-3 justify-center">
          {parsed.name && (
            <Badge variant="outline" className="text-sm py-1 px-3">
              <Bot className="h-3.5 w-3.5 mr-1.5" />
              {parsed.name}
            </Badge>
          )}
          {parsed.model && (
            <Badge variant="outline" className="text-sm py-1 px-3">
              Model: {parsed.model}
            </Badge>
          )}
          {parsed.type && (
            <Badge variant="outline" className="text-sm py-1 px-3">
              Type: {parsed.type}
            </Badge>
          )}
          {parsed.tools && parsed.tools.length > 0 && (
            <Badge variant="outline" className="text-sm py-1 px-3">
              {parsed.tools.length} tool{parsed.tools.length !== 1 ? 's' : ''}
            </Badge>
          )}
        </div>

        {parsed.description && (
          <p className="text-center text-sm text-muted-foreground italic max-w-xl mx-auto">
            {parsed.description}
          </p>
        )}

        {/* AI Suggestions */}
        {suggestions.length > 0 && (
          <div className="space-y-2">
            {suggestions.map((s, i) => (
              <Alert key={i} className="border-amber-500/30 bg-amber-500/5">
                <Lightbulb className="h-4 w-4 text-amber-500" />
                <AlertDescription className="ml-2">
                  <span className="font-medium">{s.label}</span>
                  <span className="text-muted-foreground"> — {s.detail}</span>
                </AlertDescription>
              </Alert>
            ))}
          </div>
        )}

        <Separator />

        {/* Graph builder or structured form based on agent type */}
        {parsed.type === 'graph' ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Graph Editor</Label>
              <div className="flex gap-1 rounded-md border p-0.5">
                <Button
                  size="sm"
                  variant={graphEditorMode === 'visual' ? 'secondary' : 'ghost'}
                  className="h-7 text-xs px-2.5"
                  onClick={() => setGraphEditorMode('visual')}
                >
                  Visual Editor
                </Button>
                <Button
                  size="sm"
                  variant={graphEditorMode === 'yaml' ? 'secondary' : 'ghost'}
                  className="h-7 text-xs px-2.5"
                  onClick={() => setGraphEditorMode('yaml')}
                >
                  YAML Editor
                </Button>
              </div>
            </div>
            {graphEditorMode === 'visual' ? (
              <GraphBuilder
                graphConfig={extractGraphConfig(config)}
                onChange={(gc) => setConfig(updateConfigWithGraph(config, gc))}
                resources={resources}
              />
            ) : (
              <AgentConfigForm
                config={config}
                onChange={setConfig}
                resources={resources}
              />
            )}
          </div>
        ) : (
          <AgentConfigForm
            config={config}
            onChange={setConfig}
            resources={resources}
          />
        )}

        {/* Refine input */}
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-3">
              <Label htmlFor="refine-feedback" className="flex items-center gap-2">
                <Wand2 className="h-4 w-4" />
                Refine with AI (optional)
              </Label>
              <div className="flex gap-2">
                <Textarea
                  id="refine-feedback"
                  placeholder="e.g. Add a web search tool, change the tone to be more formal, use a different model..."
                  value={refineFeedback}
                  onChange={(e) => setRefineFeedback(e.target.value)}
                  rows={2}
                  className="resize-none flex-1"
                />
                <Button
                  onClick={refineConfig}
                  disabled={refining || !refineFeedback.trim()}
                  className="self-end"
                >
                  {refining ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  <span className="ml-2">{refining ? 'Refining...' : 'Refine'}</span>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Render: Step 2
  // ---------------------------------------------------------------------------

  const renderStep2 = () => {
    const parsed = parseConfigFields(config);

    return (
      <div className="space-y-6 max-w-4xl mx-auto">
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold tracking-tight">Review & Save</h2>
          <p className="text-muted-foreground">
            Your agent configuration is ready. Copy it, download it, or open it directly in the IDE.
          </p>
        </div>

        {/* Summary card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <Bot className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">
                  {parsed.name ?? 'Untitled Agent'}
                </CardTitle>
                {parsed.description && (
                  <CardDescription>{parsed.description}</CardDescription>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2 mb-4">
              {parsed.model && (
                <Badge variant="secondary">Model: {parsed.model}</Badge>
              )}
              {parsed.type && (
                <Badge variant="secondary">Type: {parsed.type}</Badge>
              )}
              {parsed.tools?.map((tool) => (
                <Badge key={tool} variant="outline">
                  {tool}
                </Badge>
              ))}
            </div>
            <Separator className="mb-4" />
            <ConfigPreview config={config} />
          </CardContent>
        </Card>

        {/* Action buttons */}
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Button onClick={saveToContentStore}>
            <Save className="h-4 w-4 mr-2" />
            Save Agent
          </Button>
          <Button variant="outline" onClick={copyToClipboard}>
            <Copy className="h-4 w-4 mr-2" />
            Copy to Clipboard
          </Button>
          <Button variant="outline" onClick={downloadConfig}>
            <Download className="h-4 w-4 mr-2" />
            Download
          </Button>
          <Button variant="outline" onClick={openInIDE}>
            <FileCode className="h-4 w-4 mr-2" />
            Open in IDE
          </Button>
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------------------

  return (
    <div className="flex flex-col min-h-screen">
      {/* Header */}
      <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-14 items-center gap-4 px-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="gap-1"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <Separator orientation="vertical" className="h-6" />
          <div className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-semibold">Agent Wizard</h1>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 container px-4 py-8 max-w-5xl mx-auto">
        <StepIndicator current={step} />

        {step === 0 && renderStep0()}
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
      </div>

      {/* Footer navigation */}
      <div className="border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-16 items-center justify-between px-4 max-w-5xl mx-auto">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={step === 0}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          {step < 2 ? (
            <Button
              onClick={handleNext}
              disabled={!canGoNext() || generating}
            >
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          ) : (
            <Button onClick={openInIDE}>
              <Check className="h-4 w-4 mr-2" />
              Open in IDE
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default AgentWizardPage;
